import { useEffect, useMemo, useRef, useState } from "react";
import { usePage } from "./context";
import { cn, str } from "./ui";
import type { TeamPerson } from "./teams-store";
import { t } from "@/lib/i18n";

/* Модальное окно «Добавить команду»: название, описание и участники.
   Участники выбираются из списка сотрудников рабочим пространства — с поиском и чипами. */

const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "•";

type Props = {
  /** активные пользователи пространства */
  people: TeamPerson[];
  onClose: () => void;
  onSave: (team: { name: string; description: string; members: string[] }) => void;
};

function TeamModal({ people, onClose, onSave }: Props) {
  const { brand } = usePage();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [touched, setTouched] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const found = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return people;
    return people.filter((m) => `${m.name} ${m.email} ${m.role}`.toLowerCase().includes(q));
  }, [people, query]);

  const selected = people.filter((m) => picked.includes(m.id));
  const valid = name.trim().length > 0;

  const toggle = (id: string) => setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const submit = () => {
    setTouched(true);
    if (!valid) return;
    onSave({ name: name.trim(), description: description.trim(), members: picked });
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-start justify-center overflow-y-auto bg-black/55 px-4 py-8 backdrop-blur-sm md:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="team-modal-title"
        className="flash-card relative w-full max-w-lg overflow-hidden rounded-card border border-border bg-card !p-0 shadow-lift"
      >
        <div className="flex items-start gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0 flex-1">
            <h3 id="team-modal-title" className="text-[15px] font-semibold">
              {t("Добавить команду")}
            </h3>
            <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
              {t("Создайте команду, чтобы группировать пользователей. Команды можно назначать вместо отдельных пользователей, когда нужно поделиться ресурсами, например представлениями.")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("Закрыть")}
            className="grid size-7 flex-none cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <i className="ri-close-line text-base leading-none" aria-hidden />
          </button>
        </div>

        <div className="flex flex-col gap-4 px-5 py-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-[12px] font-medium text-muted-foreground">{t("Название команды")}</span>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
              placeholder={t("Введите название, например «Маркетинг»")}
              aria-invalid={touched && !valid}
              className={cn(
                "h-10 w-full rounded-btn border bg-background px-3 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/25",
                touched && !valid ? "border-red-500/70" : "border-border focus:border-primary",
              )}
            />
            {touched && !valid ? <span className="text-[11px] text-red-600">{t("Без названия команду не сохранить")}</span> : null}
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-[12px] font-medium text-muted-foreground">{t("Описание команды")}</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={4}
              placeholder={t("Опишите, чем занимается команда, например «Работа с маркетинговыми контактами»")}
              className="min-h-[104px] w-full resize-y rounded-btn border border-border bg-background px-3 py-2.5 text-[13px] leading-relaxed text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
            />
            <span className="text-end text-[10.5px] tabular-nums text-muted-foreground">{description.length} / 500</span>
          </label>

          <div className="flex flex-col gap-1.5" ref={boxRef}>
            <span className="text-[12px] font-medium text-muted-foreground">{t("Участники команды")}</span>
            <div className="relative">
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                aria-haspopup="listbox"
                className="flex min-h-10 w-full cursor-pointer items-center gap-2 rounded-btn border border-border bg-background px-2.5 py-1.5 text-start text-[12.5px] transition hover:border-primary/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
              >
                <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                  {selected.length === 0 ? (
                    <span className="text-muted-foreground">{t("Добавьте пользователя, например «Иван»")}</span>
                  ) : (
                    selected.map((m) => (
                      <span key={m.id} className="inline-flex items-center gap-1 rounded-full bg-primary/10 ps-1 pe-1.5 py-0.5 text-[11px] font-semibold text-primary">
                        <span className="grid size-4 place-items-center rounded-full bg-primary/20 text-[8px] font-bold text-primary" aria-hidden>
                          {initials(m.name)}
                        </span>
                        {m.name}
                      </span>
                    ))
                  )}
                </span>
                <i className={cn("ri-arrow-down-s-line flex-none text-base leading-none text-muted-foreground transition", open && "rotate-180")} aria-hidden />
              </button>

              {open ? (
                <div className="absolute inset-x-0 top-[calc(100%+6px)] z-40 overflow-hidden rounded-btn border border-border bg-card shadow-lift">
                  <div className="border-b border-border p-2">
                    <input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder={t("Поиск пользователей")}
                      className="h-8 w-full rounded-btn border border-border bg-background px-2.5 text-[12px] placeholder:text-muted-foreground focus:border-primary focus:outline-none"
                    />
                  </div>
                  <ul role="listbox" aria-multiselectable className="max-h-56 overflow-y-auto p-1">
                    {found.length === 0 ? (
                      <li className="px-2.5 py-3 text-[12px] text-muted-foreground">{t("Никого не нашли — измените запрос")}</li>
                    ) : (
                      found.map((m) => {
                        const on = picked.includes(m.id);
                        return (
                          <li key={m.id} role="option" aria-selected={on}>
                            <button
                              type="button"
                              onClick={() => toggle(m.id)}
                              className={cn(
                                "flex w-full cursor-pointer items-center gap-2.5 rounded-btn px-2 py-1.5 text-start transition",
                                on ? "bg-primary/10" : "hover:bg-muted",
                              )}
                            >
                              <span
                                className={cn(
                                  "grid size-7 flex-none place-items-center rounded-full text-[10px] font-bold",
                                  on ? "bg-primary text-primary-foreground" : "bg-foreground/[.06] text-foreground/70",
                                )}
                                aria-hidden
                              >
                                {initials(m.name)}
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-[12.5px] font-semibold leading-snug">{m.name}</span>
                                <span className="block truncate text-[10.5px] text-muted-foreground">
                                  {m.role} · {m.email}
                                </span>
                              </span>
                              <i
                                className={cn("flex-none text-base leading-none", on ? "ri-checkbox-circle-fill text-primary" : "ri-checkbox-blank-circle-line text-muted-foreground/60")}
                                aria-hidden
                              />
                            </button>
                          </li>
                        );
                      })
                    )}
                  </ul>
                  <div className="flex items-center justify-between gap-2 border-t border-border px-2.5 py-2 text-[10.5px] text-muted-foreground">
                    <span>
                      {t("Выбрано: {n} из {total}", { n: picked.length, total: people.length })}
                    </span>
                    {picked.length > 0 ? (
                      <button type="button" onClick={() => setPicked([])} className="cursor-pointer font-semibold text-primary hover:underline">
                        {t("Очистить")}
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
            <p className="text-[10.5px] text-muted-foreground">
              {t("Участники получат доступ к назначенным команде диалогам и контактам в {brand}.", { brand: str(brand) || "Hub" })}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border bg-background/60 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn px-3 text-[12px] font-semibold text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            {t("Отмена")}
          </button>
          <button
            type="button"
            onClick={submit}
            aria-disabled={!valid}
            className={cn(
              "inline-flex h-8 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3.5 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110",
              !valid && "cursor-not-allowed opacity-45 hover:brightness-100",
            )}
          >
            <i className="ri-save-3-line text-[13px] leading-none" aria-hidden />
            {t("Сохранить")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TeamModal;